/**
 * Orion tool result charts – Bar, Line, Area from chartHint + result.
 * Same contract as ERP OrionToolCharts; uses recharts.
 */
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#00e676', '#ff5252', '#6c63ff', '#00d4ff', '#f59e0b', '#a78bfa'];

function normalizeNum(v) {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

function prepareSeries(result, seriesKey, categoryKey, valueKeys) {
  const raw = result?.[seriesKey];
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((row) => {
    const out = { ...row };
    if (out[categoryKey] != null) out.name = String(out[categoryKey]).slice(0, 24);
    valueKeys.forEach((k) => {
      if (out[k] !== undefined) out[k] = normalizeNum(out[k]);
    });
    return out;
  });
}

function ChartBlock({ type, data, categoryKey, valueKeys, title, chartId }) {
  const hasMultiple = valueKeys.length > 1;
  const gradientId = `orion-area-${chartId}`;

  if (!data || data.length === 0) return null;

  const commonProps = { data, margin: { top: 8, right: 8, left: -8, bottom: 0 } };
  const axisStyle = { fontSize: 10, fill: 'rgba(200,200,220,0.6)' };
  const tooltipStyle = { background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 };

  if (type === 'line') {
    return (
      <div className="console-tool-chart">
        {title && <div className="console-tool-chart-title">{title}</div>}
        <ResponsiveContainer width="100%" height={200}>
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {valueKeys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} name={k.replace(/_/g, ' ')} stroke={CHART_COLORS[i % CHART_COLORS.length]} dot={{ r: 3 }} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'area') {
    return (
      <div className="console-tool-chart">
        {title && <div className="console-tool-chart-title">{title}</div>}
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS[0]} stopOpacity={0.35} />
                <stop offset="100%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {valueKeys.map((k, i) => (
              <Area key={k} type="monotone" dataKey={k} name={k.replace(/_/g, ' ')} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`url(#${gradientId})`} strokeWidth={2} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="console-tool-chart">
      {title && <div className="console-tool-chart-title">{title}</div>}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart {...commonProps} barGap={4} barSize={hasMultiple ? 14 : 24}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {valueKeys.map((k, i) => (
            <Bar key={k} dataKey={k} name={k.replace(/_/g, ' ')} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function OrionToolCharts({ result, chartHint }) {
  const blocks = useMemo(() => {
    if (!chartHint?.charts || !Array.isArray(chartHint.charts) || !result) return [];
    return chartHint.charts
      .map((c, i) => {
        const data = prepareSeries(result, c.seriesKey, c.categoryKey, c.valueKeys);
        if (data.length === 0) return null;
        return (
          <ChartBlock
            key={`${c.seriesKey}-${c.title || ''}`}
            chartId={`${c.seriesKey}-${(c.title || '').replace(/\s/g, '-')}-${i}`}
            type={c.type || 'bar'}
            data={data}
            categoryKey={c.categoryKey}
            valueKeys={c.valueKeys}
            title={c.title}
          />
        );
      })
      .filter(Boolean);
  }, [result, chartHint]);

  if (blocks.length === 0) return null;

  return (
    <div className="console-tool-charts">
      {blocks.map((block, i) => (
        <React.Fragment key={i}>{block}</React.Fragment>
      ))}
    </div>
  );
}
